import './ChartFilterLine.scss';
import './chart-tooltip.scss';
import cn from 'classnames';
import { useMemo } from 'react';
import { MdLink, MdLinkOff } from 'react-icons/md';
import { ChartConfig } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { QueryResponse } from '@blisspointmedia/bpm-types/dist/querying';
import { DimensionId } from '@blisspointmedia/bpm-types/dist/querying/ids';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Placement, Size } from '@Components/designConstants';
import { Warning800 } from '@src/utils/colors';
import { buildActiveChartFilterSegments, columnCurrencyByRef } from './chart-context-line-utils';
import { DashboardFilterChip } from './chart-filter-line-constants';
import { useFieldMetaCtx } from './useFieldMetaCtx';

interface ChartFilterLineProps {
  config: ChartConfig;
  // The response the chart is rendering, for the per-column currency a metric filter's threshold
  // is formatted in.
  data: QueryResponse;
  // The viewer's breakdown pick, if the chart has a breakdown control — the same value the chart's
  // data request was narrowed by, so a metric filter names the rows it's really tested against.
  breakdown?: DimensionId | null;
  // The dashboard's filters as this card sees them (applied or skipped). Only dashboard cards pass
  // it; without it the line renders just the chart's own filters.
  dashboardFilterChips?: DashboardFilterChip[];
}

// The chart's own filters as one line under its title, plus the dashboard's filters as chips: applied
// (link icon) or skipped (broken-link icon, reason in an info tooltip). Nothing for an unfiltered chart.
export const ChartFilterLine = ({
  config,
  data,
  breakdown,
  dashboardFilterChips = []
}: ChartFilterLineProps) => {
  const fieldMetaCtx = useFieldMetaCtx(config);
  const currencyByRef = useMemo(() => columnCurrencyByRef(data), [data]);
  const segments = buildActiveChartFilterSegments(config, breakdown, {
    fieldMetaCtx,
    currencyByRef
  });
  if (segments.length === 0 && dashboardFilterChips.length === 0) return undefined;

  return (
    <div className='chart-filter-line'>
      {segments.length > 0 && <span>Filters: {segments.join(' · ')}</span>}
      {/* The chips mirror the dashboard's filter list one to one, so position is the stable key
          (labels can repeat). */}
      {dashboardFilterChips.map((chip, index) => (
        <DashboardFilterChipView key={index} chip={chip} />
      ))}
    </div>
  );
};

interface DashboardFilterChipViewProps {
  chip: DashboardFilterChip;
}

// The note or skip reason sits on an info icon, so a mouse or keyboard user reaches it the same way.
const DashboardFilterChipView = ({ chip }: DashboardFilterChipViewProps) => {
  const tooltipText = chip.applied ? chip.granularityNote : chip.reasonText;
  return (
    <span className={cn('dashboard-filter-chip', { skipped: !chip.applied })}>
      {chip.applied ? <MdLink /> : <MdLinkOff />}
      {chip.label}
      {tooltipText && (
        <InfoTooltip
          placement={Placement.BOTTOM_START}
          popoverClassName='chart-tooltip'
          size={Size.SM}
          iconColor={chip.applied ? undefined : Warning800}
          aria-label={chip.applied ? 'Dashboard filter note' : `Why ${chip.label} is not applied`}
        >
          {tooltipText}
        </InfoTooltip>
      )}
    </span>
  );
};
